<template>
  <!-- 结果页右侧：AI 问答面板（AskFiles 接口，一问一答的普通聊天交互） -->
  <aside class="judicial-ai-side">
    <div ref="chatScrollRef" class="judicial-chat-scroll">
      <!-- 空状态提示 -->
      <div v-if="!chatMessages.length" class="judicial-chat-empty">
        <div class="judicial-chat-empty-title">AI 案件问答</div>
        <div class="judicial-chat-empty-sub">
          基于本案上传的案件材料回答你的问题
        </div>
      </div>

      <div v-for="(m, i) in chatMessages" :key="i" class="judicial-thread">
        <!-- 用户气泡 -->
        <div v-if="m.kind === 'user'" class="judicial-bubble user">
          <div class="judicial-bubble-content">{{ m.text }}</div>
          <div class="judicial-role user">我</div>
        </div>
        <!-- AI 气泡（pending 为等待回答的转圈占位） -->
        <div v-else class="judicial-bubble ai">
          <div class="judicial-role ai">AI</div>
          <div v-if="m.pending" class="judicial-bubble-content pending">
            <span class="judicial-dot"></span><span class="judicial-dot"></span
            ><span class="judicial-dot"></span>
          </div>
          <div v-else class="judicial-bubble-content">{{ m.text }}</div>
        </div>
      </div>
    </div>

    <div class="judicial-ai-input-wrap">
      <div class="judicial-ai-input">
        <textarea
          v-model="chatInput"
          :placeholder="chatPlaceholder"
          @keydown.enter.exact.prevent="sendChatMessage"
        ></textarea>
        <div class="judicial-input-footer">
          <span class="judicial-input-tip">Enter 发送，Shift + Enter 换行</span>
          <button
            class="judicial-send"
            :class="{ loading: chatSending }"
            :disabled="chatSending"
            @click="sendChatMessage"
          >
            <svg class="icon sm"><use href="#jp-send" /></svg>
          </button>
        </div>
      </div>
      <div class="judicial-disclaimer">
        内容由 AI 生成，仅供参考，请注意甄别。
      </div>
    </div>
  </aside>
</template>

<script setup>
import { ref, watch, nextTick, onUnmounted } from "vue";
import { message } from "ant-design-vue";
import { askJudgmentFiles, getJudgmentMaterials } from "@/api/judicativePaper";

const props = defineProps({
  task: { type: Object, required: true },
});

const CHAT_PLACEHOLDER =
  "请输入你的问题，例如：本案的裁判依据是什么、判项表述是否准确等...";
const chatMessages = ref([]);
const chatInput = ref("");
const chatPlaceholder = ref(CHAT_PLACEHOLDER);
const chatSending = ref(false);
const chatScrollRef = ref(null);
// 该文书的全部案件材料 ID（进入面板时调 GetMaterials 拉一次，提问时随 AskFiles 带上）
const materialIds = ref([]);
// 进行中的流式请求控制器：切换任务 / 组件卸载时中断，避免旧回答落进新会话
let chatAbort = null;

function initChat() {
  chatAbort?.abort();
  chatAbort = null;
  chatMessages.value = [];
  chatInput.value = "";
  chatPlaceholder.value = CHAT_PLACEHOLDER;
  chatSending.value = false;
  materialIds.value = [];
  loadMaterialIds();
}
watch(() => props.task, initChat, { immediate: true });
onUnmounted(() => {
  chatAbort?.abort();
  chatAbort = null;
});

// 拉取该文书全部材料的 ID：AskFiles 提问需要；失败静默（提问时 materialIds 为空数组）
async function loadMaterialIds() {
  const docId = props.task?.docId ?? props.task?.id;
  if (!docId) return;
  try {
    const res = await getJudgmentMaterials(docId);
    // 任务已切换则丢弃过期响应
    if ((props.task?.docId ?? props.task?.id) !== docId) return;
    const data = (res && (res.data || res)) || {};
    const list = Array.isArray(data)
      ? data
      : data.items || data.list || data.materials || [];
    materialIds.value = list
      .map((m) => m.id ?? m.materialId ?? m.fileId ?? null)
      .filter((x) => x !== null);
  } catch (err) {
    console.error("[JudicativePaper] getJudgmentMaterials error:", err);
  }
}

function scrollChat() {
  const box = chatScrollRef.value;
  if (box) box.scrollTop = box.scrollHeight;
}
// 发送问题：调 AskFiles 流式接口（带 docId + 全部材料 ID），占位气泡随增量
// 逐段追加（打字机效果）；取回的必须是数组内代理对象，改原始对象不触发渲染
async function sendChatMessage() {
  const text = chatInput.value.trim();
  if (!text || chatSending.value) return;
  chatInput.value = "";
  chatSending.value = true;
  chatPlaceholder.value = "正在生成...";
  chatMessages.value.push({ kind: "user", text });
  chatMessages.value.push({ kind: "ai", pending: true, text: "" });
  const pendingMsg = chatMessages.value[chatMessages.value.length - 1];
  nextTick(scrollChat);

  const t = props.task;
  chatAbort = new AbortController();
  try {
    await askJudgmentFiles({
      docId: t?.docId ?? t?.id,
      materialIds: [],
      question: text,
      signal: chatAbort.signal,
      onChunk: (chunk) => {
        pendingMsg.pending = false; // 首段增量到达即撤掉转圈
        pendingMsg.text += chunk;
        scrollChat();
      },
    });
    if (!pendingMsg.text) pendingMsg.text = "未获取到回答，请换个问法再试。";
  } catch (err) {
    // 主动中断（切任务 / 卸载）静默返回，会话已被 initChat 重置
    if (err?.name === "AbortError") return;
    // fetch 不走 http.js 拦截器，此处统一兜底：
    // 已流出部分内容时保留并标注中断；否则撤掉占位气泡、问题退回输入框
    console.error("[JudicativePaper] askJudgmentFiles error:", err);
    if (pendingMsg.text) {
      pendingMsg.pending = false;
      pendingMsg.text += "\n\n（回答中断，请重试）";
    } else {
      const idx = chatMessages.value.indexOf(pendingMsg);
      if (idx > -1) chatMessages.value.splice(idx, 1);
      chatInput.value = text;
    }
    // 后端 errMsg（中文）直接透出，其余网络类错误用兜底文案
    const msg = err?.message || "";
    message.error(
      /[\u4e00-\u9fa5]/.test(msg) ? msg : "AI 回答生成失败，请稍后重试",
    );
  } finally {
    chatSending.value = false;
    chatPlaceholder.value = CHAT_PLACEHOLDER;
    chatAbort = null;
    nextTick(scrollChat);
  }
}
</script>

<style scoped>
.judicial-ai-side {
  --brand: #1768ff;
  --line: #e5ebf3;
}
.icon {
  width: 20px;
  height: 20px;
  stroke: currentColor;
  fill: none;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.icon.sm {
  width: 16px;
  height: 16px;
}
button {
  cursor: pointer;
}
.judicial-ai-side {
  background: #fff;
  height: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.judicial-chat-scroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 20px 18px 16px;
}
.judicial-chat-empty {
  display: grid;
  place-items: center;
  align-content: center;
  gap: 6px;
  height: 100%;
  text-align: center;
  color: #8a96aa;
}
.judicial-chat-empty-title {
  font-weight: 800;
  color: #4a5c78;
  font-size: 15px;
}
.judicial-chat-empty-sub {
  font-size: 13px;
}
.judicial-thread {
  margin-bottom: 0;
}
.judicial-bubble {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin-bottom: 12px;
}
.judicial-bubble.user {
  justify-content: flex-end;
}
.judicial-bubble-content {
  max-width: 345px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #f4f6f8;
  color: #25384f;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.75;
  white-space: pre-wrap;
  word-break: break-word;
}
.judicial-bubble.ai .judicial-bubble-content {
  background: #f4f6f8;
}
.judicial-bubble-content.pending {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 16px 18px;
}
.judicial-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #9fb2cc;
  animation: judicialBlink 1.2s infinite ease-in-out;
}
.judicial-dot:nth-child(2) {
  animation-delay: 0.18s;
}
.judicial-dot:nth-child(3) {
  animation-delay: 0.36s;
}
@keyframes judicialBlink {
  0%,
  80%,
  100% {
    opacity: 0.25;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-2px);
  }
}
.judicial-role {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  flex: 0 0 auto;
  margin-top: 2px;
}
.judicial-role.user {
  background: #3b82f6;
}
.judicial-role.ai {
  background: var(--brand);
}
.judicial-ai-input-wrap {
  flex: none;
  border-top: 1px solid #e3eaf4;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 -10px 24px rgba(35, 66, 112, 0.06);
  padding: 14px 18px 16px;
  position: sticky;
  bottom: 0;
  z-index: 20;
}
.judicial-ai-input {
  border: 1px solid #dce4ef;
  border-radius: 14px;
  background: #fff;
  padding: 12px;
}
.judicial-ai-input textarea {
  width: 100%;
  height: 78px;
  min-height: 78px;
  border: 0;
  outline: 0;
  resize: none;
  color: #243552;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.7;
}
.judicial-ai-input textarea::placeholder {
  color: #9aa6ba;
}
.judicial-input-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
}
.judicial-input-tip {
  color: #a2adbe;
  font-size: 12px;
}
.judicial-send {
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 11px;
  background: var(--brand);
  color: #fff;
  display: grid;
  place-items: center;
  box-shadow: 0 8px 18px rgba(21, 104, 255, 0.22);
}
.judicial-send.loading {
  background: #1f2937;
}
.judicial-send:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}
.judicial-disclaimer {
  margin-top: 8px;
  color: #a2adbe;
  font-size: 12px;
  text-align: center;
}
</style>
